import type { LocationTreeNode } from '../../api/schedulingApis';
import { getEffectiveLocationIdForDisplay } from '../../location-hierarchy/locationScopeUtils';
import { getLeafTasks } from '../dashboard/logic/prepareDashboardData';
import { isProjectLocationType } from '../schedule-locations/logic/locationTypeRules';
import type { TaskSnapshot } from '../../stores/types';

export type ScheduleReadinessState =
  | 'needs_tasks'
  | 'needs_locations'
  | 'needs_assignment'
  | 'ready';

export type ScheduleReadinessResult = {
  state: ScheduleReadinessState;
  taskCount: number;
  leafTaskCount: number;
  assignedLeafCount: number;
  unassignedLeafCount: number;
  meaningfulLocationCount: number;
};

/** Count locations that are not project-type (site structure beyond the project root). */
export function countMeaningfulLocations(tree: LocationTreeNode[] | null | undefined): number {
  if (!tree?.length) return 0;
  let count = 0;
  const walk = (nodes: LocationTreeNode[]) => {
    for (const node of nodes) {
      if (!isProjectLocationType(node.location_type)) {
        count += 1;
      }
      if (node.child?.length) walk(node.child);
    }
  };
  walk(tree);
  return count;
}

function buildTaskGetter(tasks: TaskSnapshot[]) {
  const byId = new Map(tasks.map((t) => [String(t.id), t]));
  return (id: string) => {
    const task = byId.get(String(id));
    if (!task) return undefined;
    return {
      parentTaskId: task.parentTaskId,
      taskType: task.taskType,
      locationId: task.locationId,
    };
  };
}

/**
 * Derive schedule readiness for location↔task connectivity.
 * Priority: needs_tasks → needs_locations → needs_assignment → ready.
 */
export function deriveScheduleReadiness(
  tasks: TaskSnapshot[],
  locationTree: LocationTreeNode[] | null | undefined,
): ScheduleReadinessResult {
  const taskCount = tasks.length;
  const leafTasks = getLeafTasks(tasks);
  const leafTaskCount = leafTasks.length;
  const meaningfulLocationCount = countMeaningfulLocations(locationTree);
  const getTask = buildTaskGetter(tasks);

  let assignedLeafCount = 0;
  for (const leaf of leafTasks) {
    const effectiveId = getEffectiveLocationIdForDisplay(getTask, String(leaf.id));
    if (effectiveId) assignedLeafCount += 1;
  }
  const unassignedLeafCount = leafTaskCount - assignedLeafCount;

  if (taskCount === 0) {
    return {
      state: 'needs_tasks',
      taskCount,
      leafTaskCount,
      assignedLeafCount,
      unassignedLeafCount,
      meaningfulLocationCount,
    };
  }

  if (meaningfulLocationCount === 0) {
    return {
      state: 'needs_locations',
      taskCount,
      leafTaskCount,
      assignedLeafCount,
      unassignedLeafCount,
      meaningfulLocationCount,
    };
  }

  if (leafTaskCount > 0 && unassignedLeafCount > 0) {
    return {
      state: 'needs_assignment',
      taskCount,
      leafTaskCount,
      assignedLeafCount,
      unassignedLeafCount,
      meaningfulLocationCount,
    };
  }

  return {
    state: 'ready',
    taskCount,
    leafTaskCount,
    assignedLeafCount,
    unassignedLeafCount,
    meaningfulLocationCount,
  };
}
